import { dynamicMiddleware, rootReducer } from "@momentum/redux-store";
import { registerSW } from "virtual:pwa-register";
import {
  createAction,
  createListenerMiddleware,
  createSlice,
  miniSerializeError,
} from "@reduxjs/toolkit/react";
import type {
  PayloadAction,
  SerializedError,
  WithSlice,
} from "@reduxjs/toolkit/react";

type SwStatus =
  | {
      status: "initial";
    }
  | {
      status: "registering";
    }
  | {
      status: "registered";
      scriptUrl: string;
    }
  | {
      status: "error";
      error: SerializedError;
    };

interface SwState {
  status: SwStatus;
  offlineReady: boolean;
  needRefresh: boolean;
}

const initialState: SwState = {
  status: {
    status: "initial",
  },
  offlineReady: false,
  needRefresh: false,
};

const lazySlice = createSlice({
  name: "sw",
  initialState,
  reducers: {
    registering: (state) => {
      state.status = {
        status: "registering",
      };
    },
    registered: (
      state,
      action: PayloadAction<{
        scriptUrl: string;
      }>
    ) => {
      state.status = {
        status: "registered",
        scriptUrl: action.payload.scriptUrl,
      };
    },
    error: (state, action: PayloadAction<SerializedError>) => {
      state.status = {
        status: "error",
        error: action.payload,
      };
    },
    update: (
      state,
      action: PayloadAction<{ offlineReady?: boolean; needRefresh?: boolean }>
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },
  },
});

declare module "@momentum/redux-store" {
  export interface LazyLoadedSlices extends WithSlice<typeof lazySlice> {}
}

export const swSlice = lazySlice.injectInto(rootReducer);

export const initSw = createAction("sw/init");
export const updateSw = createAction("sw/updateSw");

const swListenerMiddleware = createListenerMiddleware({});

swListenerMiddleware.startListening({
  actionCreator: initSw,
  async effect(_action, listenerApi) {
    listenerApi.unsubscribe();
    listenerApi.cancelActiveListeners();
    const dispatch = listenerApi.dispatch;
    dispatch(swSlice.actions.registering());
    const updateSWFn = registerSW({
      onRegisteredSW(swScriptUrl) {
        dispatch(
          swSlice.actions.registered({
            scriptUrl: swScriptUrl,
          })
        );
      },
      onRegisterError(error) {
        dispatch(swSlice.actions.error(miniSerializeError(error)));
      },
      onOfflineReady() {
        dispatch(swSlice.actions.update({ offlineReady: true }));
      },
      onNeedRefresh() {
        dispatch(swSlice.actions.update({ needRefresh: true }));
      },
    });

    while (true) {
      await listenerApi.condition(updateSw.match);
      await updateSWFn();
    }
  },
});

dynamicMiddleware.addMiddleware(swListenerMiddleware.middleware);
